<template>
  <div class="container rank-report">
    <div class="rank-report__title-container">
      <div class="rank-report__title-left">
          <button class="rank-report__button rank-report__button__delete"
            @click="$modal.show('delete')"
            v-if="isUserDeveloper && isDataFetched"
          >
            Delete Reports
          </button>
        <div class="rank-report__header-button-wrapper"
              v-if="isDataFetched">
          <button
            @click.stop="cancelSortingAndFilters"
            class="rank-report__clear-filters">
            <span>clear all filters and sorting</span>
            <span class="rank-report__cross">&times;</span>
          </button>
        </div>
        <div>
        </div>

      </div>
      <div class="rank-report__title-right"
          v-if="isDataFetched">
        <div class="rank-report__rank-reports">
          <span class="rank-report__count">rank reports: {{totalItems}}</span>
          <base-select
            class="rank-report__grid-select"
            :options="paginationOptions"
            alignLeft
            :titleStyle="{'padding-left': '5px'}"
            :headerStyle="{}"
            showTopBorder
            shiftLabelRight
            v-model="pageItemsLimit"
            :selectStyle="{'max-height': '35px !important', width: '100px !important'}"
            @change="handleLimitChange"
          />
        </div>
        <button class="rank-report__button" @click="openCreateModal()">
          Create Rank Report
        </button>
      </div>
    </div>

    <div class="common-table rank-report__table" >
      <div class="common-table-container">
        <div class="common-table__row common-table__header"
          v-if="isDataFetched">
          <div class="common-table__header-cell"
              v-for="column in rankReportTableColumnsConfig"
              :key="column.property"
              :class="{[`rank-report__${column.property}`]: true}">
            <template v-if="column.property === 'edit-button'">
              <!--       Don't show anything       -->
              <base-check-box
                v-if="isUserDeveloper"
                  v-model="selectAll"
                  @change="updateAllCheckboxes($event)"
                >
                <div style="height: 26px; width: 34px;"></div>
              </base-check-box>
            </template>
            <template v-else-if="column.property === 'export-links'">
            </template>
            <base-multi-select-sortable-header
              v-else
              class="rank-report__select-header"
              :column="column"
              :sort-config="sortConfig"
              :predefinedOptions="filterParams[column.property]"
              :predefinedDateRangeFilter="['created', 'updated'].includes(column.property) ? {start: filterParams[column.property]['date_from'], end: filterParams[column.property]['date_to']}: null"
              @sort="onHeaderCellClicked(column, $event)"
              @apply="filterColumn($event, column)"
              @cancel="cancelFilter($event, column)"
              :ref="sortableHeaderRefs[column.property]"
            />
          </div>
        </div>
        <template v-if="dataMissing">
          <div class="common-table__row no-data-block">
            No records found
          </div>
        </template>
        <div class="" v-else-if="!isDataFetched">
          <div class="rank-report__loader">
            <base-loader/>
          </div>
        </div>
        <template v-else>
          <template v-for="(rankReport) in paginatedKeywords">
            <div :key="rankReport.id" class="common-table__row">
              <div class="common-table__cell"
                v-for="column in rankReportTableColumnsConfig"
                :key="column.property"
                :class="[`rank-report__${column.property}`, `rank-report__row_${column.property}`]">
                  <template v-if="column.property === 'edit-button'">
                    <base-check-box
                      v-if="isUserDeveloper"
                      v-model="rankReport.delete"
                      class="delete-checkbox"
                      @change="updateCheckbox($event)"
                      >
                      <div style="height: 26px; width: 34px;"></div>
                    </base-check-box>
                    <a @click="openCreateModal(rankReport.id)" class="edit-icon">
                      <base-material-design-icon icon="edit" />
                    </a>
                  </template>
                  <template v-else-if="column.property === 'export-links'">
                    <div>
                      <a :href="generateHrefFromEnv(rankReport.rankings_report_url)" class="rank-report__link">
                        Rankings
                      </a>
                    </div>
                    <div>
                      <a :href="generateHrefFromEnv(rankReport.comp_rankings_report_url)" class="rank-report__link">
                        Competitor Rankings
                      </a>
                    </div>
                    <div>
                      <a :href="generateHrefFromEnv(rankReport.serp_report_url)" class="rank-report__link">
                        Raw SERP
                      </a>
                    </div>
                  </template>
                  <template v-else>
                    <span class="cell-wrapper">
                      {{ rankReport[column.property] }}
                    </span>
                  </template>
              </div>
            </div>
          </template>
        </template>
      </div>
    </div>
    <div class="pagination-wrapper" v-show="hasPagination">
      <base-pagination :limit="pageItemsLimit"
                       :total-items="totalItems"
                       @pageChanged="pageChanged"/>
    </div>
    <div class="rank-report__update-loader" v-if="isDataUpdating">
      <base-loader class="rank-report__update-loader-ring"/>
    </div>

    <modal name="delete">
      <DeleteReportsPopup
        ref="delete-modal"
        :report-count="selectedReports.length"
        @apply="deleteReports"
        @cancel="$modal.hide('delete')"
        />
    </modal>
    <modal name="create"
           v-bind="changeRankReportModalProps">
      <CreateUpdateRankReportPopup
        ref="create-rank-report"
        @apply="createRankReport($event)"
        :selected-rank-report="selectedRankReport"
        :opts="opts"
        @cancel="closeCreateModal"
        @edit="pauseOrRestoreFromClientView($event)"/>
    </modal>
  </div>
</template>

<script>
import filters from '@/common/Filters'
import helper from '@/services/Helper'
import { mapActions, mapState } from 'vuex'
import baseModalProps from '@/common/BaseModalProps'
import CreateUpdateRankReportPopup from './CreateUpdateRankReportPopup'
import DeleteReportsPopup from './DeleteReportsPopup'
import ToasterService from '@/services/toasterService'
import _ from 'lodash'
import BaseSortMixins from '@/common/BaseSortMixins'
import { INTEGRATIONS_MODE } from '@/common/BaseConstants'
import rankReports from '../../store/modules/rank-reports'

const ITEMS_PER_PAGE = 100
const icons = {
  angleRight: 'angle-right',
  angleDown: 'angle-down'
}
const EMPTY_FILTERS = {
  timestamp_submitted: {
    date_from: null,
    date_to: null
  },
  timestamp_deleted: {
    date_from: null,
    date_to: null
  },
  timestamp_last_received_data: {
    date_from: null,
    date_to: null
  },
  sort_column: [],
  sort_order: [],
  page_from: 0,
  page_to: ITEMS_PER_PAGE,
}

// table config
const DEFAULT_CONFIG = [
  {
    label: '',
    property: 'edit-button'
  },
  {
    label: 'ID',
    property: 'id'
  },
  {
    label: 'Type',
    property: 'is_adhoc'
  },
  {
    label: 'Submitted',
    property: 'timestamp_submitted'
  },
  {
    label: 'Deleted',
    property: 'timestamp_deleted'
  },
  {
    label: 'Last Data Received',
    property: 'timestamp_last_received_data'
  },
  {
    label: 'Data',
    property: 'report_completion'
  },
  {
    label: 'Agg',
    property: 'snapshot_completion'
  },
  {
    label: 'Source',
    property: 'report_source'
  },
  {
    label: '',
    property: 'export-links'
  }
]

export default {
  name: 'RankReports',
  components: { DeleteReportsPopup, CreateUpdateRankReportPopup },
  mixins: [BaseSortMixins],
  // filters received form url
  props: ['filters'],
  mounted () {
  },
  data () {
    return {
      rankReports: [],
      selectAll: false,
      isDataFetched: false,
      selectedRankReport: null,
      isDataUpdating: false,
      filteredRankReports: [],
      sortConfig: [],
      pageItemsLimit: ITEMS_PER_PAGE,
      totalItems: 0,
    //   // flag notifies that filters received form url query was applied
      isQueryFiltersApplied: false,
      rankReportTableColumnsConfig: _.cloneDeep(DEFAULT_CONFIG),
      filterParams: _.cloneDeep(EMPTY_FILTERS),
      isOptimized: {
        yes: false,
        no: false
      },
      sortableHeaderRefs: {
        'timestamp_submitted': 'timestamp_submitted',
        'timestamp_deleted': 'timestamp_deleted',
        'timestamp_last_received_data': 'timestamp_last_received_data',
        'report_completion': 'report_completion',
        'snapshot_completion': 'snapshot_completion',
        'report_source': 'report_source',
        'is_adhoc': 'is_adhoc',
        'id': 'id'
      },
      paginationOptions: [
        {
          label: '100',
          value: 100
        },
        {
          label: '250',
          value: 250
        },
        {
          label: '500',
          value: 500
        }
      ],
      settings: {
      }
    }
  },
  created () {
    if (this.selectedClient) {
      this.loadData()
    }
  },
  computed: {
    ...mapState({
      selectedClient: 'selectedClient'
    }),
    isUserDeveloper () {
      return this.userRoles.includes('developer')
    },
    dataMissing () {
      return this.isDataFetched && !this.rankReports.length
    },
    changeRankReportModalProps: function () {
      const changeRankReportModalProps = {
        width: 690
      }
      return { ...baseModalProps, ...changeRankReportModalProps }
    },
    hasPagination () {
      return (this.isDataFetched && !this.isDataUpdating) && this.totalItems > this.pageItemsLimit
    },
    paginatedKeywords () {
      let rankReports = _.cloneDeep(this.rankReports)
      rankReports.forEach(report => report.delete = false)
      const { page_from = 0, page_to = 100 } = this.filterParams
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.totalItems = rankReports.length
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.filteredRankReports = rankReports
      return rankReports.slice(page_from, page_to)
    },
    selectedReports () {
      return this.paginatedKeywords.filter(report => report.delete).map(report => report.id)
    }
  },
  watch: {
    selectedClient (selectedClient) {
      this.loadData()
    }
  },
  filters: {
  },
  methods: {
    ...mapActions('rankReports', ['getRankReportData', 'createNewRankReport', 'updateRankReport', 'getRankReportOptions', 'deleteRankReports']),
    generateHrefFromEnv (href) {
      if (process.env.VUE_APP_IS_V2_API === 'true') {
        const idx = href.indexOf('rank_report')
        href = href.slice(0, idx) + 'rest/v2/' + href.slice(idx)
      }
      return href
    },
    updateAllCheckboxes (value) {
      this.selectAll = value
      this.paginatedKeywords.forEach(report => report.delete = value)
    },
    updateCheckbox (value) {
      if (!value && this.selectAll) {
        this.selectAll = false
      }
      this.updateSelectAllStatus()
    },
    updateSelectAllStatus() {
      this.selectAll = this.paginatedKeywords.every(report => report.delete);
    },
    async deleteReports () {
      this.isDataUpdating = true
      await this.deleteRankReports({ clientId: this.selectedClient._id, rankReports: this.selectedReports })
      this.$modal.hide('delete')
      await this.loadFilteredData()
      this.isDataUpdating = false
    },
    async loadData () {
      let [data, opts] = await Promise.all([this.getRankReportData({ clientId: this.selectedClient._id }), this.getRankReportOptions()])
      if (data && opts) {
        this.rankReports = data
        this.opts = opts
        this.isDataFetched = true
      }
    },
    reset () {
      this.sortConfig = []
      this.totalItems = 0
      this.rankReportTableColumnsConfig = DEFAULT_CONFIG
      this.setupFilterParams()
      this.loadPageData()
    },
    setupFilterParams () {
      // this.filters - filters received form url query as props
      if (!this.filters || this.isQueryFiltersApplied) {
        this.filterParams = { ...this.filterParams, ..._.cloneDeep(EMPTY_FILTERS) }
        return true
      }
      const filters = JSON.parse(this.filters)
      this.filterParams = _.cloneDeep({ ...this.filterParams, ...EMPTY_FILTERS, ...filters })
      if (filters.sort_column && filters.sort_column.length > 0) {
        filters.sort_column.forEach((columnName, index) => {
          const propertyName = columnName
          const sortDirection = filters.sort_order[index]
          const valuePreFormatter = null
          this.sortConfig.push({ propertyName, sortDirection, valuePreFormatter })
        })
      }
      this.isQueryFiltersApplied = true
      return true
    },
   /**
     * Change page of URLs (pagination changes)
     */
    pageChanged ({ from, to }) {
      this.filterParams.page_from = from
      this.filterParams.page_to = to
    },
    /**
     * Open {@link ./UrlRankReportPopup}
     */
    openModal (url) {
      this.changingRankReportData = url
      this.$modal.show('rank-report')
    },
   /**
     * Hide {@link ./CreateUrlPopup}
     * Create new URL
     */
    createRankReport: async function (rankReport) {
      this.isDataUpdating = true
      try {
        await this.createNewRankReport({ clientId: this.selectedClient._id, rankReport })
        await this.loadFilteredData()
        this.closeCreateModal()
      } catch (e) {
        this.$refs['create-rank-report'].showErrors(e[0])
      } finally {
        this.isDataUpdating = false
      }
    },
    pauseOrRestoreFromClientView: async function (rankReport) {
      this.isDataUpdating = true
      try {
        await this.updateRankReport({ clientId: this.selectedClient._id, rankReport })
        await this.loadFilteredData()
        const rankReportId = this.$refs['create-rank-report'].rankReport.id
        this.selectedRankReport = this.rankReports.find(rankReport => rankReport.id === rankReportId)
        this.$refs['create-rank-report'].rankReport = { ...this.selectedRankReport }
      } catch (e) {
        this.$refs['create-rank-report'].showErrors(e[0])
      } finally {
        this.isDataUpdating = false
      }
    },
    closeCreateModal () {
      this.selectedRankReport = null
      this.$modal.hide('create')
    },
    /**
     * Shows {@link ./CreateUrlPopup}
     */
    openCreateModal (rankReportId) {
      this.selectedRankReport = this.rankReports.find(rankReport => rankReport.id === rankReportId)
      this.$modal.show('create')
    },
    handleLimitChange: function (value) {
      this.pageItemsLimit = value
      this.filterParams = { ...this.filterParams, page_from: 0, page_to: value }
    },
    filterColumn: function (event, column) {
      if (column.property === 'timestamp_submitted' || column.property === 'timestamp_deleted') {
        this.filterParams[column.property].date_from = event.date_from
        this.filterParams[column.property].date_to = event.date_to
      } else {
        this.filterParams[column.property] = event
      }
      this.filterParams.page_from = 0
      this.filterParams.page_to = this.pageItemsLimit
      this.loadFilteredData()
    },
    onHeaderCellClicked (cell, direction, declineCall) {
      if (direction === 'clear') {
        this.sortConfig.shift()
        return
      }
      if (this.sortConfig.length) {
        this.sortConfig[0].propertyName = cell.property
        this.sortConfig[0].sortDirection = direction
      } else if (cell) {
        this.sortConfig = [{
          sortDirection: direction,
          propertyName: cell.property
        }]
      }
      this.filterParams = {
        ...this.filterParams,
        sort_column: this.sortConfig.map(item => item.propertyName),
        sort_order: this.sortConfig.map(item => item.sortDirection)
      }
      !declineCall && this.loadFilteredData()
    },
    cancelSortingAndFilters: function () {
      const emptyFilters = _.cloneDeep(EMPTY_FILTERS)
      this.filterParams = { ...this.filterParams, ...emptyFilters, page_to: this.pageItemsLimit }
      Object.keys(this.sortableHeaderRefs)
        .forEach(ref => {
          const [element] = this.$refs[ref]
          element.$refs[element.childRef].cancel(true)
        })
      this.isOptimized = {
        yes: false,
        no: false
      }
      this.sortConfig = []
      !this.isDataUpdating && this.loadFilteredData()
    },
    async loadFilteredData () {
      this.isDataUpdating = true
      let [data, opts] = await Promise.all([this.getRankReportData({
        clientId: this.selectedClient._id,
        filters: this.filterParams
      }), this.getRankReportOptions()])
      if (data && opts) {
        this.rankReports = data
        this.opts = opts
        this.isDataFetched = true
      }
      this.isDataUpdating = false
    },
    cancelFilter (event, column) {
      if (column.property === 'timestamp_submitted' ||
      column.property === 'timestamp_deleted') {
        this.filterParams[column.property].date_from = null
        this.filterParams[column.property].date_to = null
      } else {
        this.filterParams[column.property] = null
      }
      this.loadFilteredData()
    }
  }
}
</script>

<style scoped lang="scss">

  .common-table {
    border-radius: $rfx-default-border-radius;
    // overflow: visible;

    &__header {
      background-color: rgba(1, 22, 39, 0.1);
      border-top-right-radius: 4px;
      border-top-left-radius: 4px;
    }

    &__header-cell {
      color: $rfx-dark-march;
      font-weight: bold;
      padding: 0;
      height: 60px;
      display: flex;
      align-items: center;
      cursor: pointer;
      &:first-child {
        padding-left: 25px;
      }

      &:last-child {
        padding-right: 25px;
      }
    }

    &__cell {
      color: $rfx-dark-march;
      height: 60px;
      padding: 0;
      display: flex;
      align-items: center;
      font-size: 14px;
      border-bottom: 1px solid $rfx-light-steel;

      &:first-child {
        padding-left: 25px;
      }

      &:last-child {
        padding-right: 25px;
      }
    }

    &__row {
      border-left: 1px solid $rfx-light-steel;
      border-right: 1px solid $rfx-light-steel;
      min-width: 1340px;
    }
  }

  .common-table-container {
    overflow-x: scroll;
    display: flex;
    flex-direction: column;
  }

  .cell-wrapper {
    padding-left: 15px;
  }

  .rank-report {
    position: relative;
    min-height: 200vh!important;

    &__cross {
      font-size: 21px;
      margin-left: 5px;
      margin-top: -2px;
    }

    &__title-right {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      flex: 1 1 50%;
      height: 60px;
    }

    &__title-right > *:not(:last-child) {
      margin-right: 30px;
    }

    &__title-left {
      display: flex;
      align-items: center;
    }

    &__title-left > *:not(:last-child) {
      margin-right: 30px;
    }

    &__header-button-wrapper {
      border-radius: 15px;
      border: solid 1px $rfx-light-steel;
      background: white;
      height: 28px;
      display: flex;
      padding: 0 10px;
    }

    &__copy-button {
      flex: 1 1 50%;
      height: 100%;
      border: none;
      outline: none;
      color: $rfx-dark-march;
      white-space: nowrap;
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 90px;
      background-color: #fff;
      font-size: 11px;

      &:hover {
        opacity: 0.5;
      }
    }

    &__clear-filters {
      @extend .rank-report__copy-button;
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: auto;

      span:first-child {
        font-size: 11px;
      }
    }

    &__grid-select {
      flex-basis: 100px;
    }

    &__title-container {
      padding: 14px 0;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    &__loader {
      display: flex;
      width: 100%;
      justify-content: center;
      align-items: center;
      height: calc(100vh - 530px);
    }

    &__table {
      // position: relative;
      // display: flex;
      // flex-direction: column;
      // width: 920px;
    }

    &__update-loader {
      position: absolute;
      display: flex;
      width: 100%;
      height: 100%;
      justify-content: center;
      background-color: white;
      top: 0;
      opacity: 0.5;
      z-index: 10000;
    }

    &__update-loader-ring {
      margin-top: 200px;
    }


    &__rank-reports {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: space-between;
      height: 100%;
    }

    &__export-links,
    &__timestamp_submitted,
    &__timestamp_deleted,
    &__timestamp_last_received_data {
      flex: 1 0 120px;
      &.common-table__header-cell {
        flex: 1 0 120px;
      }
    }

    &__report_completion,
    &__snapshot_completion,
    &__is_adhoc {
      flex: 1 0 85px;
      &.common-table__header-cell {
        flex: 1 0 85px;
      }
    }

    &__snapshot_feature_completion {
      flex: 1 0 100px;
      &.common-table__header-cell {
        flex: 1 0 100px;
      }
    }

    &__edit-button {
      flex: 1 0 10px;

      &.common-table__header-cell {
        flex: 1 0 10px;
      }
    }

    &__export-links {
      display: block;
    }

    &__link {
      flex: 0 1 180px;
      padding-left: 15px;
      @include ellipsis;
      text-decoration: none;
      color: $rfx-bright-blue;
    }

    &__row {
      &_section,
      &_brief_rank-report,
      &_url {
        overflow: hidden;
      }

      &_timestamp_deleted {
        color: $rfx-red;
      }
    }

    &__button {
      width: 136px;
      padding: 0 20px;
      height: 60px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 900;
      text-transform: uppercase;
      border: none;
      outline: none;
      cursor: pointer;
      background-color: $rfx-medium-march;
      color: #fff;

      &__asana {
        margin: 15px 0;
      }
      &__delete {
        background-color: $rfx-red;
        margin-right: 10px;
      }
    }

    &__count {
      color: #b2b9be;
      font-size: 14px;
      font-weight: 600;
      text-decoration: none;
      width: 75px;
      white-space: nowrap;
    }
  }

  .pagination-wrapper {
    display: flex;
    height: 60px;
    margin-top: 10px;
    justify-content: center;
    align-items: center;
    border: 1px solid $rfx-light-steel;
    border-radius: $rfx-default-border-radius;
  }

  .no-data-block {
    border-bottom: 1px solid $rfx-light-steel;
  }

  .common-table {
    &__header-cell {
      color: $rfx-dark-march;
      font-weight: bold;
    }
  }

  .edit-icon {
    cursor: pointer;
    font-size: 20px;
    justify-self: end;
    display: grid;
    color: $rfx-dark-march;
  }

</style>
